import { AnimatePresence, motion } from 'framer-motion'
import { useRef, useState, type CSSProperties } from 'react'
import type { Member } from '../../lib/supabase'
import { clock, type TripRecap } from '../../lib/trips'
import { cardContent, readPalette, shareTripCard, type ShareOutcome } from '../../lib/tripCard'
import { localCurrency, parseCost } from '../../lib/currency'
import { haptic } from '../../lib/haptics'
import { HAPTICS } from '../../lib/constants'
import { useAutoFocus } from '../../hooks/useAutoFocus'
import { CheckIcon } from '../icons'

export interface FinishedTrip {
  recap: TripRecap
  finishedAt: Date
  /** The trip row (useShopping.finish), for the cost question and Edit trip. */
  tripId?: string
  storeName?: string | null
  /** The cost question is the finisher's (046): false hides it for anyone else. */
  askCost?: boolean
}

export type CostWriter = (tripId: string, cost: number | null, currency: string | null) => Promise<boolean>

const SHARE_NOTE: Partial<Record<ShareOutcome, string>> = {
  downloaded: 'Saved the image to your downloads.',
  failed: 'Couldn’t make the image. Try again.',
}

// A finished trip read like a workout (ux/01 "Shopping is a trip"): time in
// store, pace per item, the splits in the order the basket reached each
// section, who picked and who added — and Share, which turns it into an image
// (lib/tripCard.ts). The finisher is asked what it cost on top (optional, in
// this phone's currency). With the summary switched off in Settings it is just
// that question. Its sheet is TripSheets (components/stats), which also holds
// Edit trip.
export function TripSummary({ trip, cost, members, selfMemberId, nameOf, tintFor, showSummary = true, onCost, onEdit, onClose }: {
  trip: FinishedTrip
  /** What it cost already (the trip row): shown in the field, to anyone. */
  cost?: { cost: number; currency: string } | null
  /** Settings › Trip summary; off leaves only the cost question. */
  showSummary?: boolean
  onCost?: CostWriter
  /** Edit trip (cost, store, order, delete): only for a trip with a row. */
  onEdit?: () => void
  members: Member[]
  selfMemberId: string | null
  nameOf: (categoryId: string | null) => string
  tintFor: (categoryId: string | null) => string
  onClose: () => void
}) {
  const { recap } = trip
  const c = cardContent(recap, trip.finishedAt, nameOf, tintFor)
  const [sharing, setSharing] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  const share = async () => {
    setSharing(true)
    setNote(null)
    const palette = readPalette()
    const forCard = cardContent(recap, trip.finishedAt, nameOf, id => palette.resolve(tintFor(id)))
    const outcome = await shareTripCard(forCard, palette)
    setSharing(false)
    setNote(SHARE_NOTE[outcome] ?? null)
  }

  const who = (id: string | null) =>
    id && id === selfMemberId ? 'You' : members.find(m => m.id === id)?.display_name ?? 'Someone who left'
  const longest = Math.max(1, ...recap.splits.map(s => (recap.ms !== null ? s.ms : s.items)))

  return (
    <div className="ml-trip">
      <div className="ml-trip__head">
        <h2 className="ml-label">Trip finished</h2>
        <span className="ml-caption">{trip.storeName ? `${trip.storeName}, ${c.date}` : c.date}</span>
      </div>

      {trip.tripId && onCost && (trip.askCost !== false || cost) && <CostField tripId={trip.tripId} onCost={onCost} initial={cost} />}

      {showSummary && (<>
      <p className="ml-trip__hero">
        <span className="ml-trip__hero-num ml-num">{c.hero}</span>
        <span className="ml-label">{c.heroLabel}</span>
      </p>

      <ul className="ml-trip__stats" aria-label="Trip numbers">
        {c.stats.map(s => (
          <li key={s.label}>
            <span className="ml-trip__stat ml-num">{s.value}</span>
            <span className="ml-label">{s.label}</span>
          </li>
        ))}
      </ul>

      {c.versus && <p className="ml-trip__versus">{c.versus}</p>}

      <section className="ml-trip__section" aria-label="Splits">
        <h3 className="ml-label">Splits</h3>
        <ol className="ml-trip__splits">
          {recap.splits.map(s => (
            <li key={s.categoryId ?? 'none'} className="ml-trip__split" style={{ '--ml-tint': tintFor(s.categoryId) } as CSSProperties}>
              <span className="ml-dot" aria-hidden="true" />
              <span className="ml-trip__split-name">{nameOf(s.categoryId)}</span>
              <span className="ml-caption ml-num">{s.items === 1 ? '1 item' : `${s.items} items`}</span>
              {recap.ms !== null && <span className="ml-trip__split-time ml-num">{clock(s.ms)}</span>}
              <span className="ml-trip__bar" aria-hidden="true">
                <span style={{ width: `${Math.max(4, ((recap.ms !== null ? s.ms : s.items) / longest) * 100)}%` }} />
              </span>
            </li>
          ))}
        </ol>
      </section>

      {recap.pickedBy.length > 0 && (
        <section className="ml-trip__section" aria-label="Picked by">
          <h3 className="ml-label">Picked by</h3>
          <ul className="ml-trip__who">
            {recap.pickedBy.map(p => (
              <li key={p.memberId}>
                <span>{who(p.memberId)}</span>
                <span className="ml-num">{p.items}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {recap.addedBy.length > 1 && (
        <section className="ml-trip__section" aria-label="Added by">
          <h3 className="ml-label">Added by</h3>
          <ul className="ml-trip__who">
            {recap.addedBy.map(a => (
              <li key={a.memberId ?? 'gone'}>
                <span>{who(a.memberId)}</span>
                <span className="ml-num">{a.items}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <button type="button" className="ml-btn ml-btn--primary ml-btn--wide" disabled={sharing} onClick={share}>
        {sharing ? 'Making the image' : 'Share'}
      </button>
      </>)}
      {onEdit && <button type="button" className="ml-btn ml-btn--secondary ml-btn--wide" onClick={onEdit}>Edit trip</button>}
      <button type="button" className="ml-btn ml-btn--secondary ml-btn--wide" onClick={onClose}>Done</button>
      {note && <p className="ml-caption ml-trip__note" role="status">{note}</p>}
    </div>
  )
}


/** A saved cost as typed: "486,50" here, "486" for a whole amount; parseCost reads it back. */
const amountText = (cost: number) => new Intl.NumberFormat(undefined, {
  minimumFractionDigits: Number.isInteger(cost) ? 0 : 2, maximumFractionDigits: 2, useGrouping: false, numberingSystem: 'latn',
}).format(cost)

/**
 * "What did it cost?" — one big optional field, the currency inside it. Enter
 * or leaving the field saves; there is no Save button. Emptying a saved cost
 * clears it. Used by the summary and by Edit trip.
 */
export function CostField({ tripId, onCost, initial, autoFocus = false }: {
  tripId: string
  onCost: CostWriter
  /** What it cost already: shown in the field, ready to change. */
  initial?: { cost: number; currency: string } | null
  /** Opened by Add cost: the keyboard comes up with the sheet. */
  autoFocus?: boolean
}) {
  const [currency] = useState(() => initial?.currency ?? localCurrency())
  const [text, setText] = useState(initial ? amountText(initial.cost) : '')
  const [status, setStatus] = useState<'idle' | 'saved'>('idle')
  const [error, setError] = useState<string | null>(null)
  const saved = useRef<number | null>(initial?.cost ?? null)
  // Enter blurs the field, and the blur saves: one write, never two.
  const busy = useRef(false)
  const focus = useAutoFocus<HTMLInputElement>(autoFocus)

  const commit = async () => {
    const t = text.trim()
    const amount = t ? parseCost(t) : null
    if (t && amount === null) { setError('Type an amount, like 845 or 845,50.'); return }
    if (amount === saved.current || busy.current) return
    busy.current = true
    const ok = await onCost(tripId, amount, currency)
    busy.current = false
    if (ok) { saved.current = amount; setStatus('saved'); haptic(HAPTICS.chip) } else setError('Couldn’t save it. Try again.')
  }

  return (
    <form className="ml-cost" onSubmit={e => {
      e.preventDefault()
      const input = e.currentTarget.querySelector('input')
      if (input && document.activeElement === input) input.blur()
      else void commit()
    }}>
      <label className="ml-label" htmlFor="ml-trip-cost">What did it cost?</label>
      <div className="ml-cost__box">
        <input id="ml-trip-cost" ref={focus} className="ml-cost__input ml-num" inputMode="decimal" enterKeyHint="done"
          autoComplete="off" placeholder="0" value={text} onBlur={() => void commit()}
          onChange={e => { setText(e.target.value); setError(null); setStatus('idle') }} />
        <span className="ml-cost__currency">{currency}</span>
        <AnimatePresence>
          {status === 'saved' && (
            <motion.span key="saved" className="ml-cost__saved" role="status"
              initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}>
              <CheckIcon size={14} />Saved
            </motion.span>
          )}
        </AnimatePresence>
      </div>
      {error ? <p className="ml-caption" role="alert">{error}</p> : <p className="ml-caption">Optional. Your stats add it up.</p>}
    </form>
  )
}
